/**
 * SessionStorage key that indicates whether maintenance mode
 * should be ignored for the current browser session.
 *
 * SECURITY NOTE:
 * This flag is stored in sessionStorage on purpose.
 * sessionStorage is scoped to a single tab and is cleared
 * automatically when the tab or browser is closed.
 *
 * This reduces the risk of unintentionally persisting a
 * maintenance bypass across sessions or users.
 */
const ignoreKey = "maintenance:ignore";

/**
 * SessionStorage key that stores the secret maintenance
 * bypass token.
 *
 * SECURITY NOTE:
 * The token is considered sensitive and therefore must not
 * be persisted long-term (e.g. in localStorage).
 * Using sessionStorage ensures the token is short-lived
 * and cannot survive a browser restart.
 */
const tokenKey = "maintenance:token";

/**
 * Internal constant that holds the parameter name of the
 * URL parameter containing the secret bypass token.
 */
const bypassMaintenanceParameter = "bypass_maintenance";

/**
 * Enables maintenance mode bypass using the provided token.
 *
 * This is the preferred high-level API to activate the bypass,
 * as it guarantees a consistent state (flag + token).
 *
 * @param {string} token
 */
export const enableMaintenanceBypass = (token) => {
  if (typeof token !== "string" || token.length === 0) {
    throw new Error("Expected non-empty string token");
  }

  sessionStorage.setItem(ignoreKey, "1");
  sessionStorage.setItem(tokenKey, token);
};

/**
 * Initializes maintenance bypass state from the URL.
 *
 * This is executed immediately when the module is loaded
 * to ensure the application state is correct before any
 * API calls or route guards are evaluated.
 *
 * Expected URL format:
 *   ?bypass_maintenance=<token>
 */
const initializeFromUrl = () => {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  const bypassToken = params.get(bypassMaintenanceParameter);
  if (!bypassToken) {
    return;
  }

  enableMaintenanceBypass(bypassToken);

  // Remove ONLY the bypass parameter; keep taskId, redirectUrl, etc.
  params.delete(bypassMaintenanceParameter);

  const search = params.toString();
  const cleanedUrl = url.pathname + (search.length > 0 ? `?${search}` : "") + url.hash;

  window.history.replaceState({}, document.title, cleanedUrl);
};

/**
 * Initialize immediately on module import.
 *
 * We intentionally do this as a side-effect so the bypass state
 * is already available when other modules read it.
 */
initializeFromUrl();

/**
 * Returns whether maintenance mode is currently bypassed
 * for this browser tab.
 */
export const getIgnoreMaintenanceMode = () => {
  return sessionStorage.getItem(ignoreKey) === "1";
};

/**
 * Returns the active maintenance bypass token.
 *
 * An empty string is returned instead of null to keep
 * consumers simple and defensive.
 */
export const getMaintenanceModeSecretToken = () => {
  return sessionStorage.getItem(tokenKey);
};
